/**
 * FillsPopupWindow — the body rendered at /fills, designed to live in a
 * second browser window the trader opens via window.open() so they can
 * watch recent fills on a second monitor while working the dashboard.
 *
 * Standalone: renders WITHOUT the AuthShell sidebar chrome (mounted
 * outside <AuthedShell /> at the top-level router) and with a minimal
 * title bar carrying only the connection-status dot + a close button.
 *
 * Auth + data: JWT auto-inherits from the same-origin localStorage when
 * window.open() lands here, so apiFetch authenticates immediately. The
 * popup mounts its own useKalshiAccount() so the relay poller fills
 * accountStore.activityTicker for THIS window — there's no IPC sync
 * back to the parent tab. Doubles the relay's /api/trader/fills polling
 * rate (5s × 2 = ~24 req/min vs ~12 with one tab); acceptable.
 *
 * The Polymarket half of the tape needs no mount here: ActivityTicker
 * runs its own /api/polymarket/fills read (10s), so this window gets
 * both venues for the same reason it gets Kalshi — one extra poller per
 * open window, no cross-window sync.
 *
 * Hydra: the popup MUST mount useHydra(). This window used to claim it
 * didn't — "ActivityTicker reads the account store and the relay, no
 * marketStore lookups" — and that stopped being true once describeMarket
 * began resolving labels off the live frame. Every Kalshi row on the tape
 * costs 2-4 marketStore reads: the canonical away/home a CFB line needs
 * (#532), the floorStrike a spread/total's line comes from, and the
 * feed-supplied club names (#685). With no feed those reads return null,
 * so the popup silently disagreed with the docked rail — 1,254 of 1,254
 * live CFB totals rendered as truncated raw tickers here while the
 * dashboard showed "UNC @ TCU — CFB — 08-29" for the same fill (#721).
 *
 * Parity is therefore structural, not per-family: the two surfaces share
 * one renderer, so they agree exactly when they share one store.
 *
 * The cost is bounded. Orderbook subscriptions are needs-driven and
 * registered by views (PositionTracker, StreamRift, PropWorkbenchLive);
 * this window renders none, so it takes the markets channel only. And
 * hydra's WS multiplexes across tabs (per the `[Hydra] using SharedWorker`
 * console log on g1.gbuff.com), so opening a popup doesn't double the
 * hydra upstream — except where `type:'module'` SharedWorkers are
 * unsupported (Safari), which falls back to one direct socket per window.
 */

import { useEffect } from 'react';
import { X } from 'lucide-react';
import { ActivityTicker } from '../../components/ActivityTicker';
import { RecordGameLinksContext } from '../../components/accountRecord/AccountRecordDialog';
import { useAccountStore } from '../../stores/accountStore';
import { useKalshiAccount } from '../../hooks/useKalshiAccount';
import { useHydra } from '../../hooks/useHydra';
import { titleForRoute } from '../../lib/routing/documentTitle';

export function FillsPopupWindow() {
  // Mount the relay-account poller so this window's accountStore populates
  // independently of the parent tab. Without it the status never leaves
  // 'disconnected' and the Kalshi half of the tape stays empty here — the
  // Polymarket half would still render (#457).
  useKalshiAccount();

  // Mount the market feed so describeMarket can resolve labels here at all.
  // Without it this window renders a DIFFERENT label than the docked rail for
  // the same fill — see the Hydra note above. Attaching is ref-counted against
  // a module singleton, so this is an attach, not a second connection.
  useHydra();

  const status = useAccountStore((s) => s.status);

  // OS window title — what the trader sees in the taskbar / window switcher.
  useEffect(() => {
    const prev = document.title;
    document.title = titleForRoute('/fills');
    return () => {
      document.title = prev;
    };
  }, []);

  const dotClass =
    status === 'connected'
      ? 'bg-green-500'
      : status === 'connecting'
        ? 'bg-yellow-500 animate-pulse'
        : status === 'error'
          ? 'bg-red-500'
          : 'bg-muted-foreground/40';

  const statusLabel =
    status === 'connected'
      ? 'Connected'
      : status === 'connecting'
        ? 'Connecting…'
        : status === 'error'
          ? 'Error'
          : 'Disconnected';

  return (
    <div className="bg-background text-foreground flex h-screen flex-col">
      {/* Title bar — connection state + close button. Window itself is the
          chrome (browser title bar, OS controls), so this stays compact. */}
      <div className="border-border flex items-center justify-between border-b px-3 py-2">
        <div className="flex items-center gap-2">
          <span
            className={`inline-block h-2 w-2 rounded-full ${dotClass}`}
            title={`Kalshi ${statusLabel}`}
          />
          <h1 className="text-foreground text-sm font-semibold">Recent Fills</h1>
          {/* Venue-qualified (#457): the tape now carries Polymarket rows too,
              and an unlabelled "Disconnected" beside them reads as the whole
              window being dead. This dot only ever described Kalshi. */}
          <span
            className="text-muted-foreground text-xs"
            title="Polymarket fills load independently of the Kalshi connection"
          >
            Kalshi {statusLabel}
          </span>
        </div>
        <button
          type="button"
          onClick={() => window.close()}
          className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1 transition-colors"
          title="Close popup"
          aria-label="Close popup"
        >
          <X size={14} />
        </button>
      </div>

      {/* Ticker — scroll if exceeds the popup's height. Same shared component
          the docked rail uses, capped at RECENT_FILL_LIMIT rows across BOTH
          venues (src/lib/fills/recentFillTape.ts). */}
      <div className="flex-1 overflow-y-auto p-1">
        {/* #1184: a record opened here names a live game but never navigates this window. */}
        <RecordGameLinksContext.Provider value={false}>
          <ActivityTicker />
        </RecordGameLinksContext.Provider>
      </div>
    </div>
  );
}
